import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePlus2 } from 'lucide-react';
import { ApiError, api, type Me } from '../lib/api.js';
import { hasCapability } from '../lib/access.js';
import { ago } from '../lib/format.js';
import { Button, Panel, cn, useToast } from '../ui/index.js';
import { ArticleSectionsView } from './ArticleSectionsEditor.js';
import { FoldTitle, usePanelFold } from './PanelFold.js';

interface Solved {
  tickets: { number: number; subject: string; fix: string; cause: string | null; resolvedAt: string | null }[];
  /** False when no model could check them: word matches only. */
  judged?: boolean;
  draft: { offered: true } | { offered: false; why: string; article?: { id: string; title: string } };
}

/** The best few; the rest behind "Show all". */
const FIRST = 3;

/**
 * "Solved before": resolved tickets like this one, and what fixed them.
 *
 * For the agent only -- these are unreviewed notes. Folded to one line by
 * default, under the ticket's own settings. When the desk has solved this
 * more than once and no article covers it, it offers to draft one from
 * those fixes; the draft goes into Knowledge for review like any other.
 */
export function SolvedBefore({ tenant, number, me }: { tenant: string; number: number; me: Me | undefined }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['solved-before', tenant, number],
    queryFn: () => api.get<Solved>(`/api/v1/tenants/${tenant}/tickets/${number}/solved-before`),
    enabled: hasCapability(me, tenant, 'ticket.read.queue'),
    staleTime: 5 * 60_000,
  });
  const [open, toggle] = usePanelFold('solved-before');
  const [all, setAll] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [drafted, setDrafted] = useState<{ articleId: string; result: { title: string; body: string } } | null>(null);
  const draft = useMutation({
    mutationFn: () =>
      api.post<{ articleId: string; result: { title: string; body: string } }>(
        `/api/v1/tenants/${tenant}/tickets/${number}/solved-before/draft`,
        {},
      ),
    onSuccess: (r) => {
      setDrafted(r);
      void qc.invalidateQueries({ queryKey: ['solved-before', tenant, number] });
      toast.success('Article drafted', 'It is in Knowledge as a draft, for review before anyone sees it.');
    },
    onError: (e) => toast.error('Not drafted', e instanceof ApiError ? e.message : undefined),
  });

  if (!q.data || q.data.tickets.length === 0) return null;
  const { tickets, draft: offer } = q.data;
  const shown = all ? tickets : tickets.slice(0, FIRST);
  const flip = (n: number) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });

  const summary = (
    <>
      <span className="text-ink-tertiary">
        {tickets.length} similar solved
      </span>
      {offer.offered && !drafted && <span className="text-brand"> · draft available</span>}
    </>
  );

  return (
    <Panel title={<FoldTitle open={open} onToggle={toggle} title="Solved before" summary={summary} />}>
      {open && (
        <div className="space-y-2">
          <p className="text-2xs text-ink-tertiary">
            {q.data.judged === false
              ? 'Resolved tickets that share words with this one. No model was available to check they are the same problem. For the desk only.'
              : 'Resolved tickets with the same problem, and what fixed them. For the desk only.'}
          </p>
          <ul className="space-y-1.5">
            {shown.map((t) => {
              const isOpen = expanded.has(t.number);
              return (
                <li key={t.number} className="rounded-md border border-line-subtle p-2 text-2xs">
                  <p className="flex items-baseline gap-1.5">
                    <Link to={`/t/${tenant}/${t.number}`} className="nums shrink-0 font-medium text-brand hover:underline">
                      #{t.number}
                    </Link>
                    <span className="min-w-0 truncate text-ink-secondary" title={t.subject}>
                      {t.subject}
                    </span>
                    {t.resolvedAt && <span className="ml-auto shrink-0 text-ink-tertiary">{ago(t.resolvedAt)}</span>}
                  </p>
                  {/* A fix can be a whole email. Three lines until asked. */}
                  <button
                    type="button"
                    onClick={() => flip(t.number)}
                    className={cn('mt-1 block w-full whitespace-pre-wrap text-left text-ink', !isOpen && 'line-clamp-3')}
                    title={isOpen ? 'Show less' : 'Show all of it'}
                  >
                    {t.fix}
                  </button>
                  {isOpen && t.cause && <p className="mt-0.5 text-ink-tertiary">Cause: {t.cause}</p>}
                </li>
              );
            })}
          </ul>
          {tickets.length > FIRST && (
            <button type="button" onClick={() => setAll((v) => !v)} className="text-2xs text-brand hover:underline">
              {all ? 'Show fewer' : `Show all ${tickets.length}`}
            </button>
          )}

          {drafted ? (
            <div className="rounded-md border border-brand-line/60 bg-brand-tint/40 p-2.5">
              <p className="text-xs font-semibold text-ink">{drafted.result.title}</p>
              <div className="mt-2">
                <ArticleSectionsView body={drafted.result.body} />
              </div>
              <Link to={`/knowledge?group=${tenant}`} className="mt-2 inline-block text-2xs text-brand hover:underline">
                Open in Knowledge to edit and send for review
              </Link>
            </div>
          ) : offer.offered ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="xs" variant="secondary" leading={<FilePlus2 />} loading={draft.isPending} onClick={() => draft.mutate()}>
                Draft an article from these
              </Button>
              <span className="text-2xs text-ink-tertiary">Nothing in the knowledgebase covers this yet.</span>
            </div>
          ) : offer.article ? (
            <p className="text-2xs text-ink-tertiary">
              {offer.why[0]!.toUpperCase() + offer.why.slice(1)}: <span className="text-ink-secondary">{offer.article.title}</span>
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
